import {
  buildConfigurationText,
  ComparisonSpecType,
  DataPointsApiResponse,
  DateWindow,
  getConfigurationFromDefinition,
  getMeasureFormatString,
  getTimestampGranularity,
  isTimelessWithWarehousingDisabled,
  isValidConfiguration,
  MetricConfig,
  NullableStatus,
} from '@tableau/metrics-engine';
import { ConfigurationComponent, ConfigurationComponentProps } from '@tableau/metrics-shared-widgets-dom';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';

import { MapDispatchToProps,MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { updateMetricsData } from '~src/metrics/actions/UpdateMetricsActions';
import { IMetricInfo } from '~src/metrics/models/IMetricInfo';
import { Metrics } from '~src/metrics/resources/Metrics';
import { IMetricActions } from '~src/metrics/serverApi/types/IMetricActions';
import { selectAvailableActionsForEntity } from '~src/spacesPlatform/selectors/EntityActionsSelectors';

import { Messages } from '../../../codegen/Localize';
import {
  contentVerticalSpace,
  editBlockStyleClass,
  editButtonClass,
  fixedFlexColumn,
  metadataSectionStyle,
  metadataSubTitle,
  metadataText,
  resolveButton,
  resolveButtonContainer,
} from './MetricsStyles';

interface MetricConfigurationSectionOwnProps {
  metricInfo: IMetricInfo;
  metricDataPoints?: DataPointsApiResponse;
  onEditing?: (isEditing: boolean) => void;
  onConfigChanged?: (config?: MetricConfig) => void;
}

interface MetricConfigurationSectionStateProps {
  canEdit: boolean;
}

export type MetricConfigurationSectionProps = MetricConfigurationSectionOwnProps & MetricConfigurationSectionStateProps & MetricConfigurationSectionDispatchProps;

export interface MetricConfigurationSectionState {
  isEditing: boolean;
  isSaving: boolean;
  validationCounter: number;
  dateWindow: DateWindow | null;
  comparison: ComparisonSpecType | null;
  status: NullableStatus;
  initialConfig: MetricConfig | null;
}

export class MetricConfigurationSection extends React.PureComponent<MetricConfigurationSectionProps, MetricConfigurationSectionState> {
  public constructor(props: MetricConfigurationSectionProps) {
    super(props);

    const { comparison, window, status } = this.getConfigurationOrNull();

    const initialConfig: MetricConfig = this.getConfigurationOrNull();

    this.state = {
      isEditing: false,
      isSaving: false,
      validationCounter: 0,
      dateWindow: window,
      comparison,
      status,
      initialConfig,
    };
  }

  private getConfigurationOrNull = (): MetricConfig => {
    if (this.props.metricDataPoints?.definition === undefined) {
      return {
        comparison: null,
        window: null,
        status: { above: null, equal: null, below: null }
      };
    }

    const flags = {
      areDateWindowsEnabled: FeatureFlags.metricsDateWindowEnabled,
      isColorStatusIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
      isHistoricalComparisonsEnabled: FeatureFlags.metricsHistoricalComparisonsEnabled,
      isReferenceLineEnabled: FeatureFlags.metricsReferenceLineEnabled
    };

    return getConfigurationFromDefinition(flags, this.props.metricDataPoints.definition);
  };

  private handleEditClick = () => {
    this.setState({ isEditing: true }, () => {
      this.props.onEditing?.(true);
      // onConfigChanged needs to be called so that the time series is updated correctly
      this.props.onConfigChanged?.(this.state.initialConfig);
    });
  };

  private handleCancelClick = () => {
    // Need to restore the configuration as well so that when the user clicks to
    // edit again, it starts off with the authored configuration

    const { initialConfig } = this.state;

    this.setState({
      isEditing: false,
      dateWindow: initialConfig.window,
      comparison: initialConfig.comparison,
      status: initialConfig.status,
    });

    this.props.onConfigChanged?.(undefined);
    this.props.onEditing?.(false);
  };

  private handleSaveClick = () => {
    if (!this.state.isSaving) {
      const { dateWindow, comparison } = this.state;
      const requiresDateWindow = !isTimelessWithWarehousingDisabled(this.props.metricDataPoints?.definition);

      if (isValidConfiguration(comparison, dateWindow, requiresDateWindow)) {
        this.setState({
          isSaving: true,
        }, this.saveConfigAndStopEditing);
      } else {
        this.setState({
          validationCounter: this.state.validationCounter + 1,
        });
      }
    }
  };

private saveConfigAndStopEditing = async () => {
    const { dateWindow, comparison, status, initialConfig } = this.state;
    const config: MetricConfig = {
      comparison,
      status,
      window: dateWindow,
    };

    try {
      const result = await Metrics.updateConfiguration(this.props.metricInfo.luid, config, initialConfig);

      this.setState({
        isSaving: false,
        isEditing: !!result?.errors,
        initialConfig: config,
      });
      this.props.updateMetric?.();
      this.props.onEditing?.(false);
    } catch {
      this.setState({
        isEditing: true,
        isSaving: false,
      });
    }

  };

  private renderEmptyConfiguration = () => {
    return (
      <div style={flexRow}>
        <div style={dynamicTextColumn}>
          <div style={metadataText} data-tb-test-id={'no-configuration'}>
            {Messages.metricNoConfiguration()}
          </div>
        </div>
      </div>
    );
  };

  private renderConfiguration = () => {
    const isTimelessWithoutWarehousing = isTimelessWithWarehousingDisabled(this.props.metricDataPoints?.definition);
    const configurationText = this.props.metricDataPoints?.definition
      ? buildConfigurationText(this.props.metricDataPoints?.definition, isTimelessWithoutWarehousing)
      : undefined;

    if (!configurationText || (!configurationText.comparisonText && !configurationText.windowText)) {
      return this.renderEmptyConfiguration();
    }

    const { comparisonText, windowText } = configurationText;
    return (
      <>
        {windowText && <div style={flexRow}>
          <div style={fixedFlexColumn}>
            <div style={metadataText}>
              {Messages.metricDateRange()}
            </div>
          </div>
          <div style={dynamicTextColumn}>
            <div style={metadataText} data-tb-test-id={'metadata-date-range'}>
              {windowText}
            </div>
          </div>
          </div>}
          {comparisonText && <div style={{ ...flexRow, marginTop: contentVerticalSpace }}>
          <div style={fixedFlexColumn}>
            <div style={metadataText}>
              {Messages.metricComparison()}
            </div>
          </div>
          <div style={dynamicTextColumn}>
            <div style={metadataText} data-tb-test-id={'metadata-comparison'}>
              {comparisonText}
            </div>
          </div>
        </div>}
      </>
    );
  };

  private makeConfigurationComponentProps = (): ConfigurationComponentProps => {
    const { status, dateWindow, comparison, validationCounter, isSaving } = this.state;
    const config: MetricConfig = {
      status,
      comparison,
      window: dateWindow
    };

    return {
      isColorIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
      isDateWindowEnabled: FeatureFlags.metricsDateWindowEnabled,
      isHistoricalComparisonsEnabled: FeatureFlags.metricsHistoricalComparisonsEnabled,
      isReferenceLineEnabled: FeatureFlags.metricsReferenceLineEnabled,

      canApplyDateWindow: !isTimelessWithWarehousingDisabled(this.props.metricDataPoints?.definition),
      dateGranularity: getTimestampGranularity(this.props.metricDataPoints?.definition),
      formatString: getMeasureFormatString(this.props.metricDataPoints?.definition),
      data: this.props.metricDataPoints?.data,

      disabled: isSaving,
      validationCounter,

      selectedDateWindow: dateWindow ?? null,
      selectedComparison: comparison ?? null,
      status: status,

      onDateWindowChange: (dateWindow: DateWindow | null) => this.setState(
          {dateWindow},
          () => { this.props.onConfigChanged?.({...config, window: dateWindow});}
        ),
      onComparisonChange: (comparison: ComparisonSpecType | null) => this.setState(
          {comparison},
          () => { this.props.onConfigChanged?.({...config, comparison});}
        ),
      onStatusChange: (s: NullableStatus) => this.setState(
          {status: s},
          () => { this.props.onConfigChanged?.({...config, status: s});}
        ),
    };
  };

  private renderEditingConfiguration = () => {
    return (
      <>
        <ConfigurationComponent {...this.makeConfigurationComponentProps()} />
        <div style={{...resolveButtonContainer, marginTop: '10px'}}>
          <ButtonWidget
            style={resolveButton}
            buttonType={ButtonType.Outline}
            onClick={this.handleCancelClick}
            testId="cancel-configuration-edit"
            disabled={this.state.isSaving}
          >
            {Messages.cancel()}
          </ButtonWidget>
          <ButtonWidget
            style={resolveButton}
            buttonType={ButtonType.Primary}
            onClick={this.handleSaveClick}
            testId="save-configuration"
            showSpinner={this.state.isSaving}
          >
            {Messages.save()}
          </ButtonWidget>
        </div>
      </>
    );
  };

  public render(): JSX.Element {
    const { isEditing } = this.state;
    const canEnterEditMode = FeatureFlags.metricsEditingEnabled && this.props.canEdit && !isEditing;

    const className = [
      metadataSectionStyle,
      canEnterEditMode ? editBlockStyleClass : ''
    ].join(' ');

    const editModeHandle = canEnterEditMode ? this.handleEditClick : undefined;

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div className={className} data-tb-test-id={'metadata-configuration-container'} onClick={editModeHandle}>
        <div style={flexRow}>
          <div style={metadataSubTitle} tabIndex={-1}>{Messages.metricConfiguration()}</div>
          {canEnterEditMode &&
            <button
              className={editButtonClass}
              aria-label={Messages.metricsMetadataPanelEditConfiguration()}
              data-tb-test-id={'edit-configuration-pencil'}
            />
          }
        </div>
        {isEditing && this.renderEditingConfiguration()}
        {!isEditing && this.renderConfiguration()}
      </div>
    );
  }
}

interface MetricConfigurationSectionDispatchProps {
  readonly updateMetric?: () => void;
}

const mapDispatchToProps: MapDispatchToProps<MetricConfigurationSectionDispatchProps, MetricConfigurationSectionOwnProps> = (dispatch, ownProps) => {
  return {
    updateMetric: () => dispatch(updateMetricsData([ownProps.metricInfo], true))
  };
};

const mapStateToProps: MapStateToProps<
  MetricConfigurationSectionStateProps,
  MetricConfigurationSectionOwnProps
> = (state, ownProps) => {
  const availableActions = selectAvailableActionsForEntity(state, ownProps.metricInfo);
  return {
    canEdit: availableActions && (availableActions as IMetricActions).editConfiguration,
  };
};

export const ConnectedMetricConfigurationSection = connect(mapStateToProps, mapDispatchToProps)(MetricConfigurationSection);

const flexRow: React.CSSProperties = {
  display: 'flex',
};

const dynamicTextColumn: React.CSSProperties = {
  flex: 1,
};
